import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Description as BillIcon, ArrowBack } from "@mui/icons-material";
import "./NewBill.css";
import backgroundPattern from "../../assets/pasbookpng.png";
import AppDropdown from "../../components/AppDropdown";

const VENDOR_OPTIONS = [
    { value: "vendor1", label: "Zerlak Supplies" },
    { value: "vendor2", label: "Global Traders" },
];

const PAYMENT_TERMS_OPTIONS = [
    { value: "Due on Receipt", label: "Due on Receipt" },
    { value: "Net 15", label: "Net 15" },
    { value: "Net 30", label: "Net 30" },
    { value: "Net 45", label: "Net 45" },
];

const ACCOUNT_OPTIONS = [
    { value: "Cost of Goods Sold", label: "Cost of Goods Sold" },
    { value: "Advertising Expense", label: "Advertising Expense" },
    { value: "Office Supplies", label: "Office Supplies" },
];

const CUSTOMER_OPTIONS = [
    { value: "Customer A", label: "Customer A" },
    { value: "Customer B", label: "Customer B" },
];

const TAX_OPTIONS = [
    { value: "10", label: "10% TDS" },
    { value: "5", label: "5% TDS" },
];

export default function NewBillForm() {
    const navigate = useNavigate();
    const location = useLocation();

    const editBill = location.state?.bill || null;
    const isEdit = location.state?.isEdit === true;

    // Convert stored vendor name back to the select value used by this form.
    const getVendorValue = (vendorName) => {
        if (vendorName === "Zerlak Supplies") return "vendor1";
        if (vendorName === "Global Traders") return "vendor2";
        return vendorName || "";
    };

    const formatDateForInput = (value) => {
        if (!value) return "";

        // Already in YYYY-MM-DD format
        // if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        //     return value;
        // }

        // Convert DD-MM-YYYY to YYYY-MM-DD
        if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
            const [day, month, year] = value.split("-");
            return `${year}-${month}-${day}`;
        }

        return value;
    };

    const [formData, setFormData] = useState({
        vendorName: getVendorValue(editBill?.vendorName),
        billNumber: editBill?.billNumber || "",
        orderNumber:
            editBill?.orderNumber ||
            editBill?.referenceNumber ||
            "",
        billDate: formatDateForInput(
            editBill?.billDate || editBill?.date
        ),
        dueDate: formatDateForInput(editBill?.dueDate),
        paymentTerms: editBill?.paymentTerms || "Due on Receipt",
        subject: editBill?.subject || "",
        notes: editBill?.notes || "",
        discountPercent: editBill?.discountPercent || 0,
        taxType: editBill?.taxType || "TDS",
        selectedTax: editBill?.selectedTax || "",
        adjustment: editBill?.adjustment || 0,
    });

    const resolveVendorLabel = (value) =>
        VENDOR_OPTIONS.find((v) => v.value === value)?.label || value || "";

    const [vendorQuery, setVendorQuery] = useState(
        resolveVendorLabel(getVendorValue(editBill?.vendorName))
    );
    const [showVendorDrop, setShowVendorDrop] = useState(false);
    const vendorRef = useRef(null);

    // Keep the search box in sync when the selected vendor changes (edit load / reset).
    useEffect(() => {
        setVendorQuery(resolveVendorLabel(formData.vendorName));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.vendorName]);

    // Close the results window when clicking outside the vendor field.
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (vendorRef.current && !vendorRef.current.contains(event.target)) {
                setShowVendorDrop(false);
                setVendorQuery(resolveVendorLabel(formData.vendorName));
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.vendorName]);

    const vendorTerm = vendorQuery.trim().toLowerCase();
    const filteredVendors = VENDOR_OPTIONS.filter(
        (v) =>
            !vendorTerm ||
            v.label.toLowerCase().includes(vendorTerm) ||
            v.value.toLowerCase().includes(vendorTerm)
    );

    useEffect(() => {
        if (!isEdit || !editBill) return;

        setFormData({
            vendorName: getVendorValue(editBill.vendorName),
            billNumber: editBill.billNumber || "",
            orderNumber:
                editBill.orderNumber ||
                editBill.referenceNumber ||
                "",
            billDate: formatDateForInput(
                editBill.billDate || editBill.date
            ),
            dueDate: formatDateForInput(editBill.dueDate),
            paymentTerms:
                editBill.paymentTerms || "Due on Receipt",
            subject: editBill.subject || "",
            notes: editBill.notes || "",
            discountPercent: editBill.discountPercent || 0,
            taxType: editBill.taxType || "TDS",
            selectedTax: editBill.selectedTax || "",
            adjustment: editBill.adjustment || 0,
        });
    }, [isEdit, editBill]);

    const [items, setItems] = useState(() => {
        if (isEdit && Array.isArray(editBill?.items) && editBill.items.length > 0) {
            return editBill.items.map((item, index) => ({
                id: item.id || Date.now() + index,
                itemDetails: item.itemDetails || "",
                account: item.account || "",
                quantity: item.quantity ?? 1,
                rate: item.rate ?? 0,
                customerDetails: item.customerDetails || "",
                amount:
                    Number(item.amount) ||
                    (Number(item.quantity) || 0) *
                    (Number(item.rate) || 0),
            }));
        }

        return [
            {
                id: 1,
                itemDetails: "",
                account: "",
                quantity: 1,
                rate: 0,
                customerDetails: "",
                amount: 0,
            },
        ];
    });

    // =========================
    // ITEM CHANGE
    // =========================
    const handleItemChange = (index, field, value) => {
        const updatedItems = [...items];

        updatedItems[index][field] = value;

        if (field === "quantity" || field === "rate") {
            const qty =
                parseFloat(updatedItems[index].quantity) || 0;

            const rate =
                parseFloat(updatedItems[index].rate) || 0;

            updatedItems[index].amount = qty * rate;
        }

        setItems(updatedItems);
    };

    // =========================
    // ADD ROW
    // =========================
    const handleAddRow = () => {
        setItems([
            ...items,
            {
                id: Date.now(),
                itemDetails: "",
                account: "",
                quantity: 1,
                rate: 0,
                customerDetails: "",
                amount: 0,
            },
        ]);
    };

    // =========================
    // REMOVE ROW
    // =========================
    const handleRemoveRow = (index) => {
        if (items.length > 1) {
            setItems(items.filter((_, i) => i !== index));
        }
    };

    // =========================
    // CALCULATIONS
    // =========================
    const subTotal = items.reduce(
        (sum, item) => sum + (parseFloat(item.amount) || 0),
        0
    );

    const discountAmount =
        (subTotal *
            (parseFloat(formData.discountPercent) || 0)) /
        100;

    const total =
        subTotal -
        discountAmount +
        (parseFloat(formData.adjustment) || 0);

    // =========================
    // SAVE AS OPEN
    // =========================
    const handleSaveAsOpen = () => {
        if (!formData.vendorName) {
            alert("Please select a vendor.");
            return;
        }

        if (!formData.billNumber) {
            alert("Please enter Bill Number.");
            return;
        }

        const vendorName =
            formData.vendorName === "vendor1"
                ? "Zerlak Supplies"
                : formData.vendorName === "vendor2"
                    ? "Global Traders"
                    : formData.vendorName;

        const billData = {
            ...(isEdit && editBill ? editBill : {}),
            id: isEdit && editBill ? editBill.id : Date.now(),
            date: formData.billDate,
            billDate: formData.billDate,
            billNumber: formData.billNumber,
            orderNumber: formData.orderNumber,
            referenceNumber: formData.orderNumber,
            vendorName,
            status: "Open",
            dueDate: formData.dueDate,
            amount: Number(total.toFixed(2)),
            balanceDue: Number(total.toFixed(2)),
            paymentTerms: formData.paymentTerms,
            subject: formData.subject,
            notes: formData.notes,
            discountPercent: Number(formData.discountPercent) || 0,
            taxType: formData.taxType,
            selectedTax: formData.selectedTax,
            adjustment: Number(formData.adjustment) || 0,
            items,
        };

        const existingBills =
            JSON.parse(localStorage.getItem("bills")) || [];

        let updatedBills;

        if (isEdit && editBill) {
            updatedBills = existingBills.map((bill) =>
                bill.id === editBill.id ? billData : bill
            );
        } else {
            updatedBills = [...existingBills, billData];
        }

        localStorage.setItem(
            "bills",
            JSON.stringify(updatedBills)
        );

        alert(
            isEdit
                ? "Bill updated successfully!"
                : "Bill saved as Open successfully!"
        );

        navigate("/dashboard/bills");
    };

    // =========================
    // SAVE DRAFT
    // =========================
    const handleSaveDraft = () => {
        if (!formData.vendorName) {
            alert("Please select a vendor.");
            return;
        }

        const vendorName =
            formData.vendorName === "vendor1"
                ? "Zerlak Supplies"
                : formData.vendorName === "vendor2"
                    ? "Global Traders"
                    : formData.vendorName;

        const draftData = {
            ...(isEdit && editBill ? editBill : {}),
            id: isEdit && editBill ? editBill.id : Date.now(),
            date: formData.billDate,
            billDate: formData.billDate,
            billNumber: formData.billNumber,
            orderNumber: formData.orderNumber,
            referenceNumber: formData.orderNumber,
            vendorName,
            status: "Draft",
            dueDate: formData.dueDate,
            amount: Number(total.toFixed(2)),
            balanceDue: Number(total.toFixed(2)),
            paymentTerms: formData.paymentTerms,
            subject: formData.subject,
            notes: formData.notes,
            discountPercent: Number(formData.discountPercent) || 0,
            taxType: formData.taxType,
            selectedTax: formData.selectedTax,
            adjustment: Number(formData.adjustment) || 0,
            items,
        };

        const existingBills =
            JSON.parse(localStorage.getItem("bills")) || [];

        const updatedBills =
            isEdit && editBill
                ? existingBills.map((bill) =>
                    bill.id === editBill.id ? draftData : bill
                )
                : [...existingBills, draftData];

        localStorage.setItem(
            "bills",
            JSON.stringify(updatedBills)
        );

        alert(
            isEdit
                ? "Bill updated as Draft!"
                : "Bill saved as Draft!"
        );

        navigate("/dashboard/bills");
    };



    return (
        <div className="bill-page">

            <div
                className="bill-background-watermark"
                style={{ backgroundImage: `url(${backgroundPattern})` }}
            />

            <header className="bill-header">
                <div className="bill-header-left">
                    <div className="bill-page-icon" aria-hidden="true"><BillIcon /></div>
                    <div className="bill-page-title-wrap">
                        <h1>{isEdit ? "Edit Bill" : "New Bill"}</h1>
                        <p>Record and manage vendor bills for your business.</p>
                    </div>
                </div>
                <button
                    type="button"
                    className="bill-header-back"
                    onClick={() => navigate("/dashboard/bills")}
                    aria-label="Back to bills"
                >
                    <ArrowBack />
                </button>
            </header>

            <main className="bill-shell">
                <div className="bill-container">

                <div className="form-content">

                {/* ================= TOP DETAILS ================= */}
                <section className="zb-section-top">

                    <div className="zb-form-row">
                        <label className="zb-label required">
                            Vendor Name
                        </label>

                        <div className="zb-input-group vendor-select-group" ref={vendorRef}>

                            <div className="zb-vendor-search-wrap">

                                <input
                                    type="text"
                                    className="zb-vendor-search"
                                    placeholder="Search vendor"
                                    value={vendorQuery}
                                    autoComplete="off"
                                    onFocus={() => setShowVendorDrop(true)}
                                    onChange={(e) => {
                                        setVendorQuery(e.target.value);
                                        setShowVendorDrop(true);
                                    }}
                                />

                                {showVendorDrop && (
                                    <div className="zb-vendor-dropdown">
                                        {filteredVendors.length > 0 ? (
                                            filteredVendors.map((v) => (
                                                <div
                                                    key={v.value}
                                                    className={`zb-vendor-drop-item${v.value === formData.vendorName ? " active" : ""}`}
                                                    onClick={() => {
                                                        setFormData({
                                                            ...formData,
                                                            vendorName: v.value,
                                                        });
                                                        setVendorQuery(v.label);
                                                        setShowVendorDrop(false);
                                                    }}
                                                >
                                                    <span className="zb-vendor-avatar">
                                                        {v.label
                                                            .split(/\s+/)
                                                            .map((w) => w[0])
                                                            .join("")
                                                            .slice(0, 2)
                                                            .toUpperCase()}
                                                    </span>
                                                    <span className="zb-vendor-name">{v.label}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="zb-vendor-drop-empty">
                                                No vendors found
                                            </div>
                                        )}
                                    </div>
                                )}

                            </div>

                        </div>
                    </div>

                    <div className="zb-form-row">

                        <label className="zb-label required">
                            Bill#
                        </label>

                        <input
                            type="text"
                            value={formData.billNumber}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    billNumber: e.target.value,
                                })
                            }
                        />

                    </div>

                    <div className="zb-form-row">

                        <label className="zb-label">
                            Order Number
                        </label>

                        <input
                            type="text"
                            value={formData.orderNumber}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    orderNumber: e.target.value,
                                })
                            }
                        />

                    </div>

                    <div className="zb-form-row">

                        <label className="zb-label required">
                            Bill Date
                        </label>

                        <input
                            type="date"
                            // placeholder="dd/mm/yyyy"
                            value={formData.billDate}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    billDate: e.target.value,
                                })
                            }
                        />

                    </div>

                    <div className="zb-form-row split-row">

                        <div className="split-col">

                            <label className="zb-label">
                                Due Date
                            </label>

                            <input
                                type="date"
                                value={formData.dueDate}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        dueDate: e.target.value,
                                    })
                                }
                            />

                        </div>

                        <div className="split-col">

                            <label className="zb-label">
                                Payment Terms
                            </label>

                            <AppDropdown
                                value={formData.paymentTerms}
                                onChange={(val) =>
                                    setFormData({
                                        ...formData,
                                        paymentTerms: val,
                                    })
                                }
                                options={PAYMENT_TERMS_OPTIONS}
                                placeholder="Select payment terms"
                            />

                        </div>

                    </div>

                    <div className="zb-form-row">

                        <label className="zb-label">
                            Subject
                            <span className="zb-info-icon">
                                ⓘ
                            </span>
                        </label>

                        <textarea
                            rows="2"
                            maxLength="250"
                            placeholder="Enter a subject within 250 characters"
                            value={formData.subject}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    subject: e.target.value,
                                })
                            }
                        />

                    </div>

                </section>

                {/* ================= TRANSACTION LEVEL ================= */}
                <div className="zb-transaction-level">

                    <span className="zb-level-icon">
                        ⚙️
                    </span>

                    <span>
                        At Transaction Level
                    </span>

                    <span className="zb-caret">
                        ▼
                    </span>

                </div>

                {/* ================= ITEM TABLE ================= */}
                <section className="zb-item-table-section">

                    <div className="zb-table-header-action">

                        <button
                            type="button"
                            className="zb-bulk-actions"
                        >
                            ⚡ Bulk Actions
                        </button>

                    </div>

                    <table className="zb-table">

                        <thead>

                            <tr>

                                <th style={{ width: "30%" }}>
                                    ITEM DETAILS
                                </th>

                                <th style={{ width: "20%" }}>
                                    ACCOUNT
                                </th>

                                <th
                                    style={{ width: "10%" }}
                                    className="text-right"
                                >
                                    QUANTITY
                                </th>

                                <th
                                    style={{ width: "12%" }}
                                    className="text-right"
                                >
                                    RATE
                                </th>

                                <th style={{ width: "18%" }}>
                                    CUSTOMER DETAILS
                                </th>

                                <th
                                    style={{ width: "10%" }}
                                    className="text-right"
                                >
                                    AMOUNT
                                </th>

                                <th></th>

                            </tr>

                        </thead>

                        <tbody>

                            {items.map((item, index) => (

                                <tr key={item.id}>

                                    <td>

                                        <input
                                            type="text"
                                            placeholder="Type or click to select an item."
                                            value={item.itemDetails}
                                            onChange={(e) =>
                                                handleItemChange(
                                                    index,
                                                    "itemDetails",
                                                    e.target.value
                                                )
                                            }
                                        />

                                    </td>

                                    <td>

                                        <AppDropdown
                                            value={item.account}
                                            onChange={(val) =>
                                                handleItemChange(
                                                    index,
                                                    "account",
                                                    val
                                                )
                                            }
                                            options={ACCOUNT_OPTIONS}
                                            placeholder="Select an account"
                                        />

                                    </td>

                                    <td>

                                        <input
                                            type="number"
                                            className="text-right"
                                            value={item.quantity}
                                            onChange={(e) =>
                                                handleItemChange(
                                                    index,
                                                    "quantity",
                                                    e.target.value
                                                )
                                            }
                                        />

                                    </td>

                                    <td>

                                        <input
                                            type="number"
                                            className="text-right"
                                            value={item.rate}
                                            onChange={(e) =>
                                                handleItemChange(
                                                    index,
                                                    "rate",
                                                    e.target.value
                                                )
                                            }
                                        />

                                    </td>

                                    <td>

                                        <AppDropdown
                                            value={item.customerDetails}
                                            onChange={(val) =>
                                                handleItemChange(
                                                    index,
                                                    "customerDetails",
                                                    val
                                                )
                                            }
                                            options={CUSTOMER_OPTIONS}
                                            placeholder="Select Customer"
                                        />

                                    </td>

                                    <td className="text-right amount-display">
                                        {item.amount.toFixed(2)}
                                    </td>

                                    <td className="text-center">

                                        {items.length > 1 && (

                                            <button
                                                className="zb-delete-row"
                                                type="button"
                                                onClick={() =>
                                                    handleRemoveRow(index)
                                                }
                                            >
                                                ✕
                                            </button>

                                        )}

                                    </td>

                                </tr>

                            ))}

                        </tbody>

                    </table>

                    <div className="zb-table-footer-actions">

                        <button
                            type="button"
                            className="zb-btn-add-row"
                            onClick={handleAddRow}
                        >
                            ⊕ Add New Row
                            <span className="caret-down">
                                ▼
                            </span>
                        </button>

                    </div>

                </section>

                {/* ================= CALCULATIONS ================= */}
                <section className="zb-calc-section">

                    <div className="zb-notes-col">

                        <label className="zb-label">
                            Notes
                        </label>

                        <textarea
                            rows="4"
                            value={formData.notes}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    notes: e.target.value,
                                })
                            }
                        />

                        <small className="zb-sub-text">
                            It will not be shown in PDF
                        </small>

                        <div className="zb-attachment-box">

                            <label className="zb-label">
                                Attach File(s) to Bill
                            </label>

                            <button
                                type="button"
                                className="zb-upload-btn"
                            >
                                📤 Upload File
                                <span className="caret-down">
                                    ▼
                                </span>
                            </button>

                            <small className="zb-sub-text">
                                You can upload a maximum of 5 files,
                                10MB each
                            </small>

                        </div>

                    </div>

                    <div className="zb-totals-col">

                        <div className="zb-summary-row">

                            <label>
                                Sub Total
                            </label>

                            <span className="summary-val">
                                {subTotal.toFixed(2)}
                            </span>

                        </div>

                        <div className="zb-summary-row">

                            <span>
                                Discount
                            </span>

                            <div className="summary-input-group">

                                <input
                                    type="number"
                                    value={formData.discountPercent}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            discountPercent:
                                                e.target.value,
                                        })
                                    }
                                />

                                <span className="unit-label">
                                    %
                                </span>

                            </div>

                            <span className="summary-val">
                                {discountAmount.toFixed(2)}
                            </span>

                        </div>
                        
                        <div className="zb-summary-row">

                            <div className="zb-radio-group">

                                <label>

                                    <input
                                        type="radio"
                                        name="taxGroup"
                                        checked={
                                            formData.taxType === "TDS"
                                        }
                                        onChange={() =>
                                            setFormData({
                                                ...formData,
                                                taxType: "TDS",
                                            })
                                        }
                                    />

                                    TDS

                                </label>

                                <label>

                                    <input
                                        type="radio"
                                        name="taxGroup"
                                        checked={
                                            formData.taxType === "TCS"
                                        }
                                        onChange={() =>
                                            setFormData({
                                                ...formData,
                                                taxType: "TCS",
                                            })
                                        }
                                    />

                                    TCS

                                </label>

                            </div>

                            <div className="zb-tax-dropdown">
                                <AppDropdown
                                    value={formData.selectedTax}
                                    onChange={(val) =>
                                        setFormData({
                                            ...formData,
                                            selectedTax: val,
                                        })
                                    }
                                    options={TAX_OPTIONS}
                                    placeholder="Select a Tax"
                                />
                            </div>

                            <span className="summary-val">
                                -0.00
                            </span>

                        </div> 

                        {/* <div className="zb-summary-row">

                            <div className="zb-radio-group">

                                <label>
                                    <input
                                        type="radio"
                                        name="taxGroup"
                                        value="TDS"
                                        checked={formData.taxType === "TDS"}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                taxType: e.target.value,
                                                selectedTax: "",
                                            })
                                        }
                                    />
                                    TDS
                                </label>

                                <label>
                                    <input
                                        type="radio"
                                        name="taxGroup"
                                        value="TCS"
                                        checked={formData.taxType === "TCS"}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                taxType: e.target.value,
                                                selectedTax: "",
                                            })
                                        }
                                    />
                                    TCS
                                </label>

                            </div>

                            <select
                                className="zb-tax-select"
                                value={formData.selectedTax}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        selectedTax: e.target.value,
                                    })
                                }
                            >
                                <option value="">Select a Tax</option>

                                {formData.taxType === "TDS" ? (
                                    <>
                                        <option value="10">10% TDS</option>
                                        <option value="5">5% TDS</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="1">1% TCS</option>
                                        <option value="5">5% TCS</option>
                                    </>
                                )}
                            </select>

                            <span className="summary-val">
                                {formData.selectedTax
                                    ? `-${(
                                        (Number(subTotal || 0) *
                                            Number(formData.selectedTax)) /
                                        100
                                    ).toFixed(2)}`
                                    : "-0.00"}
                            </span>

                        </div> */}

                        <div className="zb-summary-row">

                            <div className="summary-input-group">

                                <input
                                    type="text"
                                    defaultValue="Adjustment"
                                    className="zb-adj-label"
                                />

                            </div>

                            <div className="summary-input-group">

                                <input
                                    type="number"
                                    value={formData.adjustment}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            adjustment:
                                                e.target.value,
                                        })
                                    }
                                />

                            </div>

                            <span className="summary-val">
                                {parseFloat(
                                    formData.adjustment || 0
                                ).toFixed(2)}
                            </span>

                        </div>

                        <div className="zb-summary-row total-row">

                            <span>
                                Total
                            </span>

                            <span className="summary-val">
                                {total.toFixed(2)}
                            </span>

                        </div>

                    </div>

                </section>

                <div className="zb-additional-fields">

                    <span>
                        <strong>
                            Additional Fields:
                        </strong>{" "}
                        Start adding custom fields for your
                        payments made by going to{" "}
                        <em>
                            Settings ➔ Purchases ➔ Bills.
                        </em>
                    </span>

                </div>

                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleSaveDraft}
                    >
                        Save as Draft
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSaveAsOpen}
                    >
                        {isEdit ? "Update & Open" : "Save as Open"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => navigate("/dashboard/bills")}
                    >
                        Cancel
                    </button>
                </div>

            </div>
            </main>
        </div>
    );
}